import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ButtonRow } from '@components/styles';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Field } from '@components/framework/formfield/Field.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import { AdminCompanyDetailOutDTO } from '@api/ringil-api.v2';
import { Form as FormApi, proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { ConflictErrors, DuplicateField, Form } from '@components/pages/admin/companies/adminCompanyDetailForm.ts';
import { NullableInput } from '@components/pages/admin/companies/AdminCompanyDetailInputs.tsx';
import SvgBuilding from '@icons/building.svg?react';

interface ComponentProps {
  form: FormApi<Form>;
  data: AdminCompanyDetailOutDTO;
  conflictErrors: ConflictErrors;
  setConflictErrors: React.Dispatch<React.SetStateAction<ConflictErrors>>;
  bypassCrnValidation: boolean;
  setBypassCrnValidation: (v: boolean) => void;
  bypassTinValidation: boolean;
  setBypassTinValidation: (v: boolean) => void;
  onCheckDuplicates: (values: Pick<Form, 'crn' | 'tin'>, fields: DuplicateField[]) => void;
  hasFieldChanged: (field: DuplicateField, value?: string | null) => boolean;
  onSave: () => void;
  saving: boolean;
}

const names = proxiedPropertiesOf<Form>();

const CompanySection: React.FC<ComponentProps> = ({
  form,
  data,
  conflictErrors,
  setConflictErrors,
  bypassCrnValidation,
  setBypassCrnValidation,
  bypassTinValidation,
  setBypassTinValidation,
  onCheckDuplicates,
  hasFieldChanged,
  onSave,
  saving,
}) => {
  const { tr } = useTranslationLgs();

  return (
    <SectionPanel
      icon={<SvgBuilding className={'w-6 h-6'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('generic.company', 'Company')}
      dataTestId={'admin-company-section-company'}>
      <div className={'grid grid-cols-1 lg:grid-cols-3 gap-4 items-start'}>
        <Field label={tr('adminCompanyDetail.name', 'Company name')}>
          <InputText value={form.values.name || ''} onChange={e => form.setFieldValue(names.name, e.target.value)} />
        </Field>
        <Field label={tr('adminCompanyDetail.companyCode', 'Company code')}>
          <NullableInput value={form.values.code} defaultValue={''} onChange={v => form.setFieldValue(names.code, v)}>
            <InputText value={form.values.code} onChange={e => form.setFieldValue(names.code, e.target.value)} />
          </NullableInput>
        </Field>
        <Field label={tr('adminCompanyDetail.companyId', 'Company ID')} readOnly>
          <InputText value={data.companyId} data-testid={'admin-company-id'} />
        </Field>

        <Field label={tr('adminCompanyDetail.crn', 'CRN')} required errorMessage={conflictErrors.crn}>
          <InputText
            value={form.values.crn || ''}
            onBlur={e => {
              if (hasFieldChanged('crn', e.target.value)) {
                onCheckDuplicates({ crn: e.target.value, tin: form.values.tin }, ['crn']);
              }
            }}
            onChange={e => {
              form.setFieldValue(names.crn, e.target.value);
              setConflictErrors(prev => ({ ...prev, crn: undefined }));
            }}
          />
        </Field>
        <Field label={tr('adminCompanyDetail.tin', 'TIN')} errorMessage={conflictErrors.tin}>
          <NullableInput
            value={form.values.tin}
            defaultValue={''}
            onChange={v => {
              form.setFieldValue(names.tin, v);
              setConflictErrors(prev => ({ ...prev, tin: undefined }));
            }}
          >
            <InputText
              value={form.values.tin || ''}
              onBlur={e => {
                if (hasFieldChanged('tin', e.target.value)) {
                  onCheckDuplicates({ crn: form.values.crn, tin: e.target.value }, ['tin']);
                }
              }}
              onChange={e => {
                form.setFieldValue(names.tin, e.target.value);
                setConflictErrors(prev => ({ ...prev, tin: undefined }));
              }}
            />
          </NullableInput>
        </Field>
        <div className={'lg:col-start-1'}>
          <RingilInputSwitch
            value={bypassCrnValidation}
            onChange={value => {
              setBypassCrnValidation(value);
              if (value) {
                setConflictErrors(prev => ({ ...prev, crn: undefined }));
              }
            }}
            label={tr('Registration.bypassCrnValidation', 'Bypass CRN validation')}
          />
        </div>
        <RingilInputSwitch
          value={bypassTinValidation}
          onChange={value => setBypassTinValidation(value)}
          label={tr('Registration.bypassTinValidation', 'Bypass TIN validation')}
        />

        <div className={'lg:col-start-1'}>
          <Field label={tr('generic.streetNr', 'Street and house number')} readOnly>
            <InputText value={data.streetAndNr} data-testid={'admin-company-street'} />
          </Field>
        </div>
        <Field label={tr('generic.city', 'City')} readOnly>
          <InputText value={data.city} data-testid={'admin-company-city'} />
        </Field>
        <Field label={tr('generic.postalCode', 'Postal code')} readOnly>
          <InputText value={data.zipCode} data-testid={'admin-company-zip'} />
        </Field>
        <Field label={tr('generic.country', 'Country')} readOnly>
          <InputText value={data.country} data-testid={'admin-company-country'} />
        </Field>
        <Field label={tr('AdminCompanyDetail.defaultTimezone', 'Default Timezone')}>
          <InputTimezone value={form.values.defaultTimezoneId} onChange={v => form.setFieldValue(names.defaultTimezoneId, v)} classNameWrapper={'w-full'} />
        </Field>
      </div>
      <ButtonRow>
        <Button label={tr('generic.save', 'Save')} onClick={onSave} loading={saving} disabled={saving} />
      </ButtonRow>
    </SectionPanel>
  );
};

export default CompanySection;
